// React and Router imports
import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";

// Third-party library imports
import ReactQuill, { Quill } from "react-quill-new";
import useUnsavedChangesGuard from "../../utils/useUnsavedChangesGuard";
import useTemplatePrefill, { readLastParentForTemplate, rememberParentForTemplate } from "../../utils/useTemplatePrefill";
import useTemplateSnippets, { insertTemplateToolbarHandler, decorateInsertTemplateButton } from "../../utils/useTemplateSnippets";
import { editorHasContent } from "../../utils/contentTransforms";
import { FONT_TOOLBAR_ENTRY } from "../../utils/quillFonts";
import Select from "react-select";
import ImageResize from "quill-image-resize-module-react";
import QuillTableBetter from "quill-table-better";
import Tesseract from "tesseract.js";
import * as pdfjsLib from "pdfjs-dist";

// Firebase imports
import { auth } from "../../firebase.js";

// Component imports
import Sidebar from "../shared/Sidebar.js";
import CommandCenterFilePickerModal from '../shared/CommandCenterFilePickerModal';
import { useContextMenu } from "../shared/ContextMenu";
import { useQuickLinks } from "../../contexts/QuickLinksContext";
import WorkspaceSidebar from "./WorkspaceSidebar.js";

// Snapshot utilities
import { useAutoWorkspaceSnapshot } from "../../utils/teamWorkspaceSnapshotUtils";

// Utility imports
import { debounce } from "../../utils/debounceUtils.js";

// Task Reference
import "../shared/TaskReferenceBlot";
import TaskReferenceDropdown from "../shared/TaskReferenceDropdown";
import { useTaskReference } from "../../utils/useTaskReference";
import "../shared/UserMentionBlot";
import UserMentionDropdown from "../shared/UserMentionDropdown";

// Styles imports
import "quill-better-table/dist/quill-better-table.css";
import "react-quill-new/dist/quill.snow.css";
import "react-quill/dist/quill.snow.css";
import "../../css/Create.css";
import "../../css/Account.css";
import "../../css/EditDocument.css";
import "../../css/DocumentationMainPage.css";
import "../../css/QuillOverrides.css";
import "../../css/TaskReference.css";
import "../../css/DocEditorMakeover.css";

// PDF Worker Configuration
pdfjsLib.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.js";

Quill.register({ "modules/table-better": QuillTableBetter }, true);
Quill.register("modules/imageResize", ImageResize);

// Quill Core Imports
const Inline = Quill.import("blots/inline");
// const Keyboard = Quill.import('modules/keyboard');
const Block = Quill.import("blots/block");

// Keyboard Bindings Configuration
// const bindings = {
//         multiLineBlockquoteEnter: {
//             key: Keyboard.keys.ENTER,
//             format: ['multi-line-blockquote'],
//             handler: function(range, context) {
//                 const quill = this.quill;
//                 quill.insertText(range.index, '\n', 'multi-line-blockquote', true);
//                 quill.setSelection(range.index + 1);
//                 return false;
//             }
//         }
// };

const FullscreenModule = function (quill, options) {
  const container = quill.container;
  let isFullscreen = false;

  // Create button element to add to toolbar
  this.toggleFullscreen = function () {
    isFullscreen = !isFullscreen;

    if (isFullscreen) {
      container
        .closest(".EditDocument__quill-container")
        .classList.add("fullscreen-mode");
      document.body.style.overflow = "hidden";
    } else {
      container
        .closest(".EditDocument__quill-container")
        .classList.remove("fullscreen-mode");
      document.body.style.overflow = "";
    }

    // Trigger a window resize event to ensure the editor adjusts its layout
    window.dispatchEvent(new Event("resize"));
  };
};

// Custom Inline Code Block
class InlineCode extends Inline {
  static create() {
    const node = super.create();
    node.setAttribute("class", "inline-code");
    return node;
  }

  static formats(node) {
    return true;
  }
}

InlineCode.blotName = "inline-code";
InlineCode.tagName = "code";

// Custom Multi-line Blockquote
class MultiLineBlockquote extends Block {
  static create() {
    const node = super.create();
    node.setAttribute("class", "multi-line-blockquote");
    return node;
  }
  static formats(node) {
    return true;
  }
}

MultiLineBlockquote.blotName = "multi-line-blockquote";
MultiLineBlockquote.tagName = "multi-line-blockquote";
MultiLineBlockquote.className = "multi-line-blockquote";

// Register Custom Quill Components
Quill.register(MultiLineBlockquote);
Quill.register(InlineCode);

function CreateWorkspaceDocument() {
  const [loadingMessage, setLoadingMessage] = useState('Loading...');
  const [selectedType, setSelectedType] = useState("");
  const [isPublic, setIsPublic] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [showTableOfContents, setShowTableOfContents] = useState(true); // BBD-58 — spaces only

  // External view-only sharing with people outside the workspace (BBD-60)
  const [externalEmails, setExternalEmails] = useState([]);
  const [currentExternalEmail, setCurrentExternalEmail] = useState("");
  const [hasSpaces, setHasSpaces] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [content, setContent] = useState("");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [allDocumentation, setAllDocumentation] = useState([]);
  const { enhancedRightClickEnabled } = useQuickLinks();
  const [selectedSpace, setSelectedSpace] = useState("");
  const [selectedParent, setSelectedParent] = useState("");
  const [uid, setUid] = useState("");
  const [authLoading, setAuthLoading] = useState(true);
  const [userEmail, setUserEmail] = useState("");
  const [showSplitView, setShowSplitView] = useState(window.innerWidth > 1200);
  const [selectedViewDoc, setSelectedViewDoc] = useState(null);
  const [selectedViewDocName, setSelectedViewDocName] = useState("");
  const [viewDocContent, setViewDocContent] = useState("");
  const [viewDocTitle, setViewDocTitle] = useState("");
  const [hasWorkspace, setHasWorkspace] = useState(true);
  const [workspaceDetails, setWorkspaceDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [emails, setEmails] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [ownerIsSubscriber, setOwnerIsSubscriber] = useState(false);
  const [workspaceTheme, setWorkspaceTheme] = useState(null);
  const [currentEmail, setCurrentEmail] = useState("");
  const [themeApplied, setThemeApplied] = useState(false);
  const [documentationLoaded, setDocumentationLoaded] = useState(false);
  const [attachmentFile, setAttachmentFile] = useState(null);
  const [ccPickerOpen, setCcPickerOpen] = useState(false);
  const [attachmentDescription, setAttachmentDescription] = useState("");
  const quillRef = useRef(null);
  const { taskRefProps, userMentionProps } = useTaskReference(quillRef, { mode: "workspace", workspaceId: workspaceDetails?.id });

  // Document templates (BBD-106/107/108/110): gallery → fields → server render →
  // prefill this form; kits are created server-side and we navigate to the
  // new space. The workspace default template (if any) is applied on load.
  const appliedTemplateRef = useRef(null);
  const templatePrefill = useTemplatePrefill({
    workspaceId: workspaceDetails?.id || null,
    workspaceDomain: workspaceDetails?.custom_domain || null,
    applyWorkspaceDefault: true,
    ready: Boolean(workspaceDetails?.id),
    hasContent: () => Boolean(name.trim() || description.trim()) || editorHasContent(content),
    apply: (rendered, template) => {
      appliedTemplateRef.current = template.ref;
      if (rendered.doc_type) setSelectedType(rendered.doc_type);
      setName(rendered.name || "");
      setDescription(rendered.description || "");
      setContent(rendered.content || "");
      if (rendered.doc_type === "Page") {
        const remembered = readLastParentForTemplate(template.ref);
        if (remembered && allDocumentation.some((doc) => String(doc.value) === String(remembered))) {
          setSelectedParent(remembered);
        }
      }
    },
    clear: () => {
      appliedTemplateRef.current = null;
      setName("");
      setDescription("");
      setContent("");
    },
  });
  const templateSnippets = useTemplateSnippets(quillRef, { workspaceId: workspaceDetails?.id || null });
  const navigate = useNavigate();

  // Snapshot state variables
  const [snapshotStatus, setSnapshotStatus] = useState('');
  const { snapshotFrequencyMinutes } = useQuickLinks();

  // Duplicate detection states
  const [duplicateSuggestions, setDuplicateSuggestions] = useState([]);
  const [duplicateCheckLoading, setDuplicateCheckLoading] = useState(false);
  const [duplicateCheckError, setDuplicateCheckError] = useState(null);
  const [showDuplicatesSuggestions, setShowDuplicatesSuggestions] = useState(false);

  const PERMISSION_OPTIONS = [
    { value: "ADMIN", label: "Administrator" },
    { value: "EDITOR", label: "Editor" },
    { value: "VIEWER", label: "Viewer" },
  ];
  const maxLengths = {
    Space: 100,
    Page: 150,
  };
  const currentMaxLength = maxLengths[selectedType] || 100;

  // Prepare document data for snapshots
  const documentData = {
    type: selectedType.toLowerCase(),
    name: name,
    content: content,
    description: description,
    parentPage: selectedType === 'Page' ? selectedParent : undefined,
    isPublic: isPublic,
    completed: completed
  };

  // Auto-snapshot hook for team workspace documents
  const { startAutoSnapshot, stopAutoSnapshot, saveManualSnapshot } = useAutoWorkspaceSnapshot(
    workspaceDetails?.id,
    documentData,
    snapshotFrequencyMinutes,
    name && content && name.trim() !== '' && content.trim() !== '' // Only enable when we have name and content
  );

  useEffect(() => {
    setLoadingMessage('Checking authentication...');
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        setUid(user.uid);
        setUserEmail(user.email);
        setLoadingMessage('Loading workspace...');
      } else {
        setUid(null);
        setUserEmail(null);
        setLoadingMessage('Not authenticated.');
        // For unauthenticated users, mark as ready immediately
        setThemeApplied(true);
        setDocumentationLoaded(true);
      }
      setAuthLoading(false);
    });
    return () => {
      unsubscribe();
    };
  }, []);

  // Handle window resize for split view
  useEffect(() => {
    const handleResize = () => {
      setShowSplitView(window.innerWidth > 1200);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  });

  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === "Escape" && isFullscreen) {
        setIsFullscreen(false);
      }
    };

    document.addEventListener("keydown", handleEscKey);
    return () => {
      document.removeEventListener("keydown", handleEscKey);
    };
  }, [isFullscreen]);

  useEffect(() => {
    if (uid) {
      setLoadingMessage('Loading workspace...');
      checkWorkspace();
    }
  }, [uid]);

  useEffect(() => {
    if (workspaceDetails) {
      setLoadingMessage('Loading workspace documentation...');
      populateWorkspaceDocumentation();
    }
  }, [workspaceDetails]);

  // Effect to check if everything is loaded and ready
  useEffect(() => {
    if (documentationLoaded && themeApplied && !authLoading) {
      // For authenticated users, also check if we have workspace details or know we don't have workspace
      if (!uid || workspaceDetails || !hasWorkspace) {
        setTimeout(() => setLoading(false), 300);
      }
    }
  }, [workspaceDetails, documentationLoaded, themeApplied, authLoading, uid, hasWorkspace]);

  


  // Add this after the ReactQuill component in the return statement
  useEffect(() => {
    if (quillRef.current) {
      // Get the toolbar element
      const toolbar = quillRef.current.getEditor().getModule('toolbar');
      
      // Find the undo and redo buttons and replace their content with Font Awesome icons
      const undoButton = toolbar.container.querySelector('.ql-undo');
      const redoButton = toolbar.container.querySelector('.ql-redo');
      
      if (undoButton) {
        undoButton.innerHTML = '<i class="fas fa-undo"></i>';
        undoButton.title = 'Undo';
      }
      
      if (redoButton) {
        redoButton.innerHTML = '<i class="fas fa-redo"></i>';
        redoButton.title = 'Redo';
      }
      decorateInsertTemplateButton(toolbar);
    }
  }, [quillRef.current]);

  // Auto-snapshot management
  useEffect(() => {
    if (workspaceDetails?.id && name && content) {
      startAutoSnapshot();
    } else {
      stopAutoSnapshot();
    }

    // Cleanup on unmount
    return () => stopAutoSnapshot();
  }, [workspaceDetails?.id, name, content, startAutoSnapshot, stopAutoSnapshot]);

  // Manual snapshot handler
  const handleManualSnapshot = async () => {
    setSnapshotStatus('saving');
    try {
      const success = await saveManualSnapshot();
      if (success) {
        setSnapshotStatus('success');
        setTimeout(() => setSnapshotStatus(''), 2000);
      } else {
        setSnapshotStatus('error');
        setTimeout(() => setSnapshotStatus(''), 3000);
      }
    } catch (error) {
      setSnapshotStatus('error');
      setTimeout(() => setSnapshotStatus(''), 3000);
    }
  };

  // Duplicate detection - check for similar documents
  const checkForDuplicates = async (title, description, type, workspaceId) => {
    // Minimum character requirements
    const MIN_TITLE_LENGTH = 3;
    const MIN_DESCRIPTION_LENGTH = 5;

    if (!type || !title || title.trim().length < MIN_TITLE_LENGTH || !workspaceId) {
      setDuplicateSuggestions([]);
      setShowDuplicatesSuggestions(false);
      return;
    }

    // Only check if description meets minimum length OR is empty (optional)
    if (description && description.trim().length < MIN_DESCRIPTION_LENGTH) {
      return; // Don't check yet if description is being typed but incomplete
    }

    setDuplicateCheckLoading(true);
    setDuplicateCheckError(null);

    try {
      const user = auth.currentUser;
      const token = user ? await user.getIdToken() : null;

      if (!token) {
        console.error("User is not authenticated.");
        setDuplicateCheckLoading(false);
        return;
      }

      const requestBody = {
        document_type: type.toLowerCase(),
        title: title.trim(),
        ...(description && description.trim().length >= MIN_DESCRIPTION_LENGTH && { description: description.trim() }),
      };

      const response = await fetch(
        `https://bluebirddocumentationadmin.pythonanywhere.com/workspaces/${workspaceId}/analyze-duplicates/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `${token}`,
            "auth-token": `Bearer ${token}`,
          },
          body: JSON.stringify(requestBody),
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.potential_duplicates && data.potential_duplicates.length > 0) {
          setDuplicateSuggestions(data.potential_duplicates);
          setShowDuplicatesSuggestions(true);
        } else {
          setDuplicateSuggestions([]);
          setShowDuplicatesSuggestions(false);
        }
      } else {
        console.error("Failed to check for duplicates:", response.status);
        setDuplicateSuggestions([]);
        setShowDuplicatesSuggestions(false);
      }
    } catch (error) {
      console.error("Error checking for duplicates:", error);
      setDuplicateCheckError("Unable to check for similar documents");
    } finally {
      setDuplicateCheckLoading(false);
    }
  };

  // Debounced duplicate check
  const debouncedCheckForDuplicates = debounce(checkForDuplicates, 800);

  // Effect to trigger duplicate check when name, description, or selectedType changes
  useEffect(() => {
    if (uid && selectedType && name && workspaceDetails?.id) {
      debouncedCheckForDuplicates(name, description, selectedType, workspaceDetails.id);
    } else {
      setDuplicateSuggestions([]);
      setShowDuplicatesSuggestions(false);
    }
  }, [name, description, selectedType, uid, workspaceDetails?.id]);

  // Fetch selected view document content (for split view)
  useEffect(() => {
    const fetchViewDocument = async () => {
      if (!selectedViewDoc || !workspaceDetails || !workspaceDetails.id) return;
      setLoadingMessage('Fetching side view document...');

      try {
        const user = auth.currentUser;
        if (!user) return;

        const token = await user.getIdToken();

        const viewDocItem = allDocumentation.find(
          (item) => item.value === selectedViewDoc
        );
        if (!viewDocItem) return;

        let endpoint;
        if (viewDocItem.type === "space") {
          endpoint = `https://bluebirddocumentationadmin.pythonanywhere.com/workspaces/${workspaceDetails.id}/spaces/${selectedViewDoc}/`;
        } else {
          endpoint = `https://bluebirddocumentationadmin.pythonanywhere.com/workspaces/${workspaceDetails.id}/pages/${selectedViewDoc}/`;
        }

        const response = await fetch(endpoint, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "auth-Token": `Bearer ${token}`,
            "Authorization": `${token}`,
            "Requesting-User-Uid": user.uid,
          },
        });

        if (response.ok) {
          const viewDocData = await response.json();
          setViewDocContent(viewDocData.content || "");
          setViewDocTitle(
            viewDocData.name || viewDocData.page_name || "Document"
          );
        }
      } catch (error) {
        console.error("Error fetching view document:", error);
      }
    };

    if (selectedViewDoc) {
      fetchViewDocument();
    }
  }, [selectedViewDoc, workspaceDetails, allDocumentation]);

  const populateWorkspaceDocumentation = async () => {
    setLoadingMessage('Loading workspace documentation...');
    try {
      const user = auth.currentUser;
      const token = user ? await user.getIdToken() : null;
      const userUid = user ? user.uid : null;

      if (!token || !workspaceDetails) {
        setLoadingMessage('User is not authenticated or workspace details are missing.');
        setDocumentationLoaded(true); // Mark as loaded even on auth error
        return;
      }

      const workspaceId = workspaceDetails.id;

      // Fetch spaces and pages concurrently
      setLoadingMessage('Fetching spaces and pages...');
      const [spacesResponse, pagesResponse] = await Promise.all([
        fetch(
          `https://bluebirddocumentationadmin.pythonanywhere.com/workspaces/${workspaceId}/spaces/`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              "auth-Token": `Bearer ${token}`,
              "Authorization": `${token}`,
              "Requesting-User-Uid": userUid,
            },
          }
        ),
        fetch(
          `https://bluebirddocumentationadmin.pythonanywhere.com/workspaces/${workspaceId}/pages/`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              "auth-Token": `Bearer ${token}`,
              "Authorization": `${token}`,
              "Requesting-User-Uid": userUid,
            },
          }
        ),
      ]);

      // Process spaces data
      const spacesData =
        spacesResponse.status === 200 ? await spacesResponse.json() : [];
      const spaceOptions = Array.isArray(spacesData)
        ? spacesData.map((space) => ({
            label: space.name,
            value: space.id,
            type: "space",
          }))
        : [];
      setHasSpaces(spaceOptions.length > 0);

      // Process pages data
      const pagesData = pagesResponse.status === 200 ? await pagesResponse.json() : [];
      const pageOptions = Array.isArray(pagesData)
        ? pagesData.map((page) => ({
            label: page.page_name,
            value: page.id,
            type: "page",
          }))
        : [];

      // Combine options and update state
      setAllDocumentation([...spaceOptions, ...pageOptions]);
      setDocumentationLoaded(true);
    } catch (error) {
      console.error("Error fetching workspace documentation:", error);
      setDocumentationLoaded(true); // Still mark as loaded even if there's an error
    }
  };

  const checkWorkspace = async () => {
    setLoadingMessage('Checking workspace...');
    try {
      const user = auth.currentUser;
      const token = user ? await user.getIdToken() : null;
      const userUid = user ? user.uid : null;
      if (!token) {
        console.error("User is not authenticated.");
        return;
      }
      setLoadingMessage('Fetching workspace list...');
      const response = await fetch(
        "https://bluebirddocumentationadmin.pythonanywhere.com/workspaces/",
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "auth-Token": `Bearer ${token}`,
              "Authorization": `${token}`,
            "Requesting-User-Uid": userUid,
          },
        }
      );
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to fetch workspace");
      }
      const data = await response.json();
      const workspaceExists = data && data.length > 0;
      if (!workspaceExists) {
        setHasWorkspace(false);
        setLoadingMessage('No workspace found.');
        // Mark as loaded even when no workspace so loading state can complete
        setThemeApplied(true);
        setDocumentationLoaded(true);
        return;
      }

      // Find the workspace where the user is OWNER
      const ownerWorkspace = data.find(
        (workspace) => workspace.role === "OWNER"
      );

      if (ownerWorkspace) {
        setOwnerIsSubscriber(ownerWorkspace.owner_is_subscriber);
        setWorkspaceTheme(ownerWorkspace.theme)
      }

      // If no owner workspace found, use the first one as fallback
      const workspaceId = ownerWorkspace ? ownerWorkspace.id : data[0].id;

      setLoadingMessage('Fetching workspace details...');
      const detailsResponse = await fetch(
        `https://bluebirddocumentationadmin.pythonanywhere.com/workspaces/${workspaceId}/`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "auth-Token": `Bearer ${token}`,
              "Authorization": `${token}`,
            "Requesting-User-Uid": userUid,
          },
        }
      );
      if (!detailsResponse.ok) {
        const errorData = await detailsResponse.json();
        throw new Error(errorData.error || "Failed to get workspace details.");
      }
      const detailsData = await detailsResponse.json();
      setWorkspaceDetails(detailsData);
      setLoadingMessage('Workspace details loaded.');
    } catch (error) {
      console.error("Error in checking workspaces: ", error);
      setHasWorkspace(false);
      // Mark as loaded even on error so loading state can complete
      setThemeApplied(true);
      setDocumentationLoaded(true);
      setLoading(false);
    }
  };

  // Unsaved-changes guard (BBD-56): anything typed into the form counts as
  // unsaved work until the document is successfully created.
  const hasUnsavedChanges =
    Boolean(name.trim() || description.trim() || attachmentFile) ||
    editorHasContent(content);
  const { releaseGuard } = useUnsavedChangesGuard(hasUnsavedChanges);

  const handleSubmit = async () => {
      // Check for duplicate document name (case-insensitive)
      // Extract the actual document name from labels (no prefix in this component's data)
      const nameExists = allDocumentation.some(doc => (doc.label || "").toLowerCase() === name.trim().toLowerCase());
      if (nameExists) {
        alert("A document with this name already exists in this workspace. Please choose a different name.");
        return;
      }
    // Validation checks
    if (!selectedType) {
      alert("Please select a content type (Space or Page).");
      return;
    }

    if (!name || name.trim() === "") {
      alert("Please enter a name for your document.");
      return;
    }

    // Check that name only contains letters and numbers
    if (!/^[a-zA-Z0-9\s\.\-\_]+$/.test(name)) {
      alert(
        "Document name must contain only letters, numbers, spaces, periods, dashes, and underscores."
      );
      return;
    }

    if (selectedType === "Page" && !selectedParent) {
      alert("Please select a parent for the page.");
      return;
    }

    // Set loading to true immediately after validation passes
    setIsSubmitting(true);
    setLoadingMessage('Submitting document...');

    try {
      const user = auth.currentUser;
      const token = user ? await user.getIdToken() : null;

      if (!token || !workspaceDetails) {
        throw new Error(
          "User is not authenticated or workspace details are missing."
        );
      }

      const workspaceId = workspaceDetails.id;

      // Find parent type if this is a page
      let parentType = null;

      if (selectedType === "Page" && selectedParent) {
        const parentDoc = allDocumentation.find(
          (doc) => doc.value === selectedParent
        );
        if (parentDoc) {
          parentType = parentDoc.type;
        }
      }

      // Prepare document data using FormData to support file uploads
      const formData = new FormData();
      
      // Use the correct field name based on document type
      if (selectedType === "Page") {
        formData.append("page_name", name);
      } else {
        formData.append("name", name);
      }
      
      formData.append("description", description);
      formData.append("is_public", isPublic);
      formData.append("completed", completed);
      formData.append("content", content);
      if (selectedType === "Space") {
        formData.append("show_table_of_contents", showTableOfContents ? "true" : "false");
      }

      // Add parent reference for pages based on type
      if (selectedType === "Page" && selectedParent) {
        if (parentType === "space") {
          formData.append("space_id", selectedParent);
        } else {
          formData.append("parent_page_id", selectedParent);
        }
      }

      // Add attachment if provided
      if (attachmentFile) {
        formData.append("attachment", attachmentFile);
        if (attachmentDescription) {
          formData.append("attachmentDescription", attachmentDescription);
        }
      }

      // Add document-specific permissions if specified
      if (emails.length > 0) {
        formData.append("permissions", JSON.stringify(emails.map((email) => ({
          email: email.email,
          role: email.role,
        }))));
      }

      // External view-only shares with people outside the workspace (BBD-60)
      if (externalEmails.length > 0) {
        formData.append("external_emails", JSON.stringify(externalEmails));
      }

      // Extract mention UIDs from Quill content and include for notifications
      const mentionedUids = (() => {
        const parser = new DOMParser();
        const parsed = parser.parseFromString(content, 'text/html');
        const spans = parsed.querySelectorAll('[data-user-uid]');
        const uids = [...new Set([...spans].map(s => s.getAttribute('data-user-uid')).filter(Boolean))];
        return uids.filter(u => u !== uid);
      })();
      if (mentionedUids.length > 0) {
        formData.append('mentionedUids', JSON.stringify(mentionedUids));
      }

      // Select endpoint based on document type
      const endpoint =
        selectedType === "Space"
          ? `https://bluebirddocumentationadmin.pythonanywhere.com/workspaces/${workspaceId}/spaces/create/`
          : `https://bluebirddocumentationadmin.pythonanywhere.com/workspaces/${workspaceId}/pages/create/`;

      // Make API request
      setLoadingMessage('Creating document...');
      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          // Don't set Content-Type header when using FormData
          "auth-Token": `Bearer ${token}`,
              "Authorization": `${token}`,
          "Requesting-User-Uid": uid,
        },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.error || `Failed to create ${selectedType.toLowerCase()}`
        );
      }

      const responseData = await response.json();
      if (appliedTemplateRef.current && selectedType === "Page" && selectedParent) {
        rememberParentForTemplate(appliedTemplateRef.current, selectedParent);
      }
      releaseGuard();

      // Navigate to the newly created document
      if (selectedType === "Space") {
        // For spaces, use the space ID directly
        const spaceId = responseData.id || responseData.space_id || responseData.space?.id;
        navigate(`/workspace/${workspaceDetails.custom_domain}/space/${spaceId}`);
      } else {
        // For pages, use the page ID directly
        const pageId = responseData.id || responseData.page_id || responseData.page?.id;
        navigate(`/workspace/${workspaceDetails.custom_domain}/page/${pageId}`);
      }
    } catch (error) {
      console.error(`Error creating ${selectedType.toLowerCase()}:`, error);
      alert(`Failed to create ${selectedType.toLowerCase()}: ${error.message}`);
      setIsSubmitting(false); // Stop loading on error
    }
  };

    const loggedOutOptions = [
        {
            label: "Login",
            icon: <i className="fas fa-sign-in-alt"></i>,
            url: "/login",
            onClick: () => navigate("/login"),
        },
        {
            label: "Register",
            icon: <i className="fas fa-user-plus"></i>,
            url: "/register",
            onClick: () => navigate("/register"),
        },
        {
            label: "Try My Notebook",
            icon: <i className="fas fa-book"></i>,
            url: "/my-notebook",
            onClick: () => navigate("/my-notebook"),
        },
        {
            label: "Visit YouTube Channel",
            icon: <i className="fab fa-youtube"></i>,
            url: "https://www.youtube.com/@schoolofintology4062",
            onClick: () => window.open("https://www.youtube.com/@schoolofintology4062", "_blank", "noopener,noreferrer"),
        },
        {
            label: "Join Discord",
            icon: <i className="fab fa-discord"></i>,
            url: "https://discord.gg/DYkESHwgVA",
            onClick: () => window.open("https://discord.gg/DYkESHwgVA", "_blank", "noopener,noreferrer"),
        },
    ];

  const loggedInOptions = [
      {
        label: "Submit Document",
        icon: <i className="fas fa-paper-plane"></i>,
        onClick: handleSubmit,
      },
      {
        label: "Home",
        icon: <i className="fas fa-home"></i>,
        url: "/",
        onClick: () => window.open("/", "_blank", "noopener,noreferrer"),
      },
      {
        label: "Your Documents",
        url: "/documentation",
        onClick: () => window.open("/documentation", "_blank", "noopener,noreferrer"),
      },
      {
        label: "Your Walkthroughs",
        url: "/walkthroughs",
        onClick: () => window.open("/walkthroughs", "_blank", "noopener,noreferrer"),
      },
      {
        label: "Your Notebook",
        url: "/my-notebook",
        onClick: () => window.open("/my-notebook", "_blank", "noopener,noreferrer"),
      },
      {
        label: "Your Workspace",
        url: "/workspaces",
        onClick: () => window.open("/workspaces", "_blank", "noopener,noreferrer"),
      },
      {
        label: "Account Settings",
        url: `/account/${uid}`,
        onClick: () => window.open(`/account/${uid}`, "_blank", "noopener,noreferrer"),
      },
    ];

    const menuOptions = uid ? loggedInOptions : loggedOutOptions;
    const { showContextMenu, ContextMenuComponent, handleContextMenu } = useContextMenu(menuOptions);

  const FullscreenModule = function (quill, options) {
    const container = quill.container;
    let isFullscreen = false;

    // Create button element to add to toolbar
    this.toggleFullscreen = function () {
      isFullscreen = !isFullscreen;

      if (isFullscreen) {
        container
          .closest(".EditDocument__quill-container")
          .classList.add("fullscreen-mode");
        document.body.style.overflow = "hidden";
      } else {
        container
          .closest(".EditDocument__quill-container")
          .classList.remove("fullscreen-mode");
        document.body.style.overflow = "";
      }

      // Trigger a window resize event to ensure the editor adjusts its layout
      window.dispatchEvent(new Event("resize"));
    };
  };

  // Handle adding an email to the permissions list
  const handleAddEmail = () => {
    if (
      currentEmail &&
      !emails.some((member) => member.email === currentEmail)
    ) {
      setEmails([
        ...emails,
        {
          email: currentEmail,
          role: "EDITOR", // Default role
        },
      ]);
      setCurrentEmail("");
    } else if (emails.some((member) => member.email === currentEmail)) {
      alert("This email has already been added.");
    } else {
      alert("Please enter a valid email address.");
    }
  };

  // Handle removing an email from the permissions list
  const handleRemoveEmail = (index) => {
    const updatedEmails = [...emails];
    updatedEmails.splice(index, 1);
    setEmails(updatedEmails);
  };

  // Handle changing a user's permission role
  const handleRoleChange = (index, newRole) => {
    const updatedEmails = [...emails];
    updatedEmails[index].role = newRole;
    setEmails(updatedEmails);
  };

  // External view-only sharing handlers (BBD-60)
  const handleAddExternalEmail = () => {
    const email = currentExternalEmail.trim().toLowerCase();
    if (!email || !email.includes("@")) {
      alert("Please enter a valid email address.");
      return;
    }
    if (externalEmails.includes(email)) {
      alert("This email has already been added.");
      return;
    }
    setExternalEmails([...externalEmails, email]);
    setCurrentExternalEmail("");
  };

  const handleRemoveExternalEmail = (index) => {
    const updated = [...externalEmails];
    updated.splice(index, 1);
    setExternalEmails(updated);
  };

    // Handle file attachment selection
    const handleAttachmentUpload = (event) => {
        const file = event.target.files[0];
        if (file) {
            // List of allowed extensions
            const allowedExtensions = ['.zip', '.pdf', '.jpeg', '.png', '.mp3', '.mp4'];

            // Get the file extension including the dot
            const fileExtension = `.${file.name.split('.').pop().toLowerCase()}`;

            // Check if the file extension is in the allowed list
            if (!allowedExtensions.includes(fileExtension)) {
            alert('Please select a ZIP, JPEG, PNG, MP3, MP4, or PDF file.');
            event.target.value = ''; // Clear the input
            return;
            }

            // Validate file size (8MB limit)
            const maxSize = 8 * 1024 * 1024; // 8MB in bytes
            if (file.size > maxSize) {
            alert('File size must be less than 8MB.');
            event.target.value = ''; // Clear the input
            return;
            }

            setAttachmentFile(file);
        }
    };

  const removeAttachment = () => {
    setAttachmentFile(null);
    setAttachmentDescription('');
    // Clear the file input
    const fileInput = document.getElementById('attachment-upload');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const truncateFileName = (fileName, maxLength = 40) => {
    if (fileName.length <= maxLength) return fileName;
    const extension = fileName.split('.').pop();
    const nameWithoutExt = fileName.substring(0, fileName.lastIndexOf('.'));
    const truncatedName = nameWithoutExt.substring(0, maxLength - extension.length - 4);
    return `${truncatedName}...${extension}`;
  };

  const modules = useMemo(
    () => ({
      toolbar: {
        container: [
          [FONT_TOOLBAR_ENTRY],
          [{ header: 1 }, { header: 2 }, { header: 3 }, { header: 4 }],
          [{ size: ["small", false, "large"] }],
          ["bold", "italic", "underline", "strike"],
          [{ color: [] }, { background: [] }],
          [{ script: "sub" }, { script: "super" }],
          [{ list: "ordered" }, { list: "bullet" }, { list: "check" }],
          [{ indent: "-1" }, { indent: "+1" }],
          [{ align: [] }],
          ["blockquote", "code-block"],
          ["link", "image", "video"],
          ["inline-code"],
          ["formula"],
          ["table-better"], // table button
          ["clean"],
          ["image"],
          ["insert-template"], // Insert a snippet template (BBD-108)
          ["undo", "redo"], // Add undo and redo buttons
        ],
        handlers: {
          undo: function() {
            this.quill.history.undo();
          },
          redo: function() {
            this.quill.history.redo();
          },
          "insert-template": insertTemplateToolbarHandler
        }
      },
      history: {
        delay: 1000,
        maxStack: 100,
        userOnly: true
      },
      imageResize: {
        parchment: Quill.import("parchment"),
        modules: ["Resize", "DisplaySize"],
      },
      table: false,
      "table-better": {
        language: "en_US",
        menus: [
          "column",
          "row",
          "merge",
          "table",
          "cell",
          "wrap",
          "copy",
          "delete",
        ],
        toolbarTable: true,
        tableCreationAttributes: {
          className: 'responsive-table',
          style: 'width: 100%; max-width: 100%;'
        }
      },
      keyboard: {
        bindings: QuillTableBetter.keyboardBindings,
      },
    }),
    []
  );
  const handleHomeButtonClick = () => {
    navigate("/");
  };
  const handleLoginButtonClick = () => {
    navigate("/login");
  };
  const handleRegisterButtonClick = () => {
    navigate("/register");
  };
  const handleCreateTeamWorkspaceButtonClick = () => {
    navigate("/workspaces");
  };
  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };
  const handleSelectedType = (e) => {
    setSelectedType(e.target.value);
  };
  const handleToggle = (e) => {
    setIsPublic(e.target.checked);
  };
  const handleCompletedToggle = (e) => {
    setCompleted(e.target.checked);
  };

  if (authLoading || (uid && loading)) {
    return (
      <div className="loading-animation-div" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '40vh' }}>
        <div className="coffee-cup-loader">
          <div className="cup">
            <div className="cup-body"></div>
            <div className="cup-handle"></div>
            <div className="cup-base"></div>
            <div className="cup-shadow"></div>
            <div className="steam steam1"></div>
            <div className="steam steam2"></div>
            <div className="steam steam3"></div>
          </div>
        </div>
        <p style={{ color: '#5B92E5', fontSize: '1.1rem', marginTop: 16 }}>{loadingMessage || 'Loading...'}</p>
      </div>
    );
  }

  // Add this right after the initial loading check
  if (isSubmitting) {
    return (
      <div className="loading-animation-div" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '40vh' }}>
        <div className="coffee-cup-loader">
          <div className="cup">
            <div className="cup-body"></div>
            <div className="cup-handle"></div>
            <div className="cup-base"></div>
            <div className="cup-shadow"></div>
            <div className="steam steam1"></div>
            <div className="steam steam2"></div>
            <div className="steam steam3"></div>
          </div>
        </div>
        <p style={{ color: '#5B92E5', fontSize: '1.1rem', marginTop: 16 }}>{loadingMessage || 'Submitting document...'}</p>
      </div>
    );
  }

  if (!uid) {
    return (
      <div 
        className="DocumentationMainPage__div DocEditor DocEditor--ws"
        onContextMenu={handleContextMenu}
    >
        <div className="DocumentationMainPage__main-content">
          <p className="large">It looks like you're not signed in.</p>
          <p className="medium">
            <strong>
              To create a document, you must have an account with us
            </strong>
            . If you already have an account, you can login by clicking the
            Login button below. If you don't already have an account, you can
            create a new one by clicking the Register button below.
          </p>
          <button
            onClick={handleHomeButtonClick}
            style={{ marginRight: "10px" }}
            className="ConflictResolver__button"
          >
            Home
          </button>
          <button
            onClick={handleLoginButtonClick}
            style={{ marginRight: "10px" }}
            className="ConflictResolver__button"
          >
            Login
          </button>
          <button
            onClick={handleRegisterButtonClick}
            className="ConflictResolver__button"
          >
            Register
          </button>
        </div>
        {enhancedRightClickEnabled && ContextMenuComponent}
      </div>
    );
  }

  if (!hasWorkspace) {
    return (
      <div 
        className="DocumentationMainPage__div DocEditor DocEditor--ws"
      onContextMenu={handleContextMenu}
    >
        <div className="DocumentationMainPage__main-content">
          <p className="large">
            It looks like you don't have a Team Workspace yet.
          </p>
          <p className="medium">
            To create a Team Workspace, you can simply click the button below.
          </p>
          <button
            onClick={handleCreateTeamWorkspaceButtonClick}
            style={{ marginRight: "10px" }}
            className="ConflictResolver__button"
          >
            Home
          </button>
          <button
            onClick={handleLoginButtonClick}
            className="ConflictResolver__button"
          >
            Login
          </button>
        </div>
        {enhancedRightClickEnabled && ContextMenuComponent}
      </div>
    );
  }

  return (
    <div 
        className="TeamWorkspaceMainPage__div DocEditor DocEditor--ws"
      onContextMenu={handleContextMenu}
    >
      <WorkspaceSidebar
        workspaceId={workspaceDetails?.id}
        customDomain={workspaceDetails?.custom_domain}
        canCreate={true}
        workspaceTheme={workspaceTheme}
      />

      <div className="TeamWorkspaceMainPage__main-content">
        <h2>Create a Workspace Space/Page</h2>
        <div className="TplStart__row">
          <button type="button" className="TplStart__btn" onClick={templatePrefill.openGallery} disabled={templatePrefill.busy}>
            <i className="fas fa-puzzle-piece"></i>&nbsp; Start from a template
          </button>
          {templatePrefill.defaultApplied && (
            <button type="button" className="TplStart__btn" onClick={templatePrefill.startBlank}>
              Start blank
            </button>
          )}
          {templatePrefill.defaultApplied && <span className="TplStart__note">This workspace's default template was applied.</span>}
          {templatePrefill.error && <span className="TplStart__error">{templatePrefill.error}</span>}
        </div>

        <div className="Create__required-fields-div">

        <div className="Create__form-group Create__required-field-margin-top">
          <select
            className="form-select Create__input Create__required-field-select"
            aria-label="Default select example"
            name="selected_type"
            value={selectedType}
            onChange={handleSelectedType}
            required
          >
            <option value="">- Select Content Type -</option>
            <option value="Space">Space</option>
            <option value="Page" disabled={!hasSpaces}>
              Page
            </option>
          </select>
        </div>
        {selectedType === "Page" && (
          <div className="Create__form-group">
            <label style={{ paddingTop: "3px" }} className="Create__required-field">
              Select a parent space or page:{" "}
            </label>
            <Select
              options={allDocumentation}
              value={allDocumentation.find(
                (option) => option.value === selectedParent
              )}
              onChange={(option) =>
                setSelectedParent(option ? option.value : "")
              }
              placeholder="Select a Space"
              isSearchable={true}
              menuPortalTarget={document.body}
              menuPosition="fixed"
              styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
            />
          </div>
        )}

        <div className="Create__form-group">
          <label className="Create__required-field">Name: </label>
          <input
            type="text"
            id="name"
            className="Create__input-short-field Create__required-field-select"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={currentMaxLength}
            placeholder={`Max ${currentMaxLength} characters`}
            required
          />
        </div>

        {/* Duplicate Detection Alert */}
        {showDuplicatesSuggestions && duplicateSuggestions.length > 0 && (
          <div className="Create__duplicate-suggestions">
            <div className="Create__duplicate-header">
              <i className="fas fa-exclamation-triangle Create__duplicate-icon"></i>
              <strong>Similar Documents Found ({duplicateSuggestions.length})</strong>
            </div>
            <p className="Create__duplicate-subtitle">
              We found documents with similar titles or descriptions. Consider reviewing them before creating a new one (click to view):
            </p>
            {duplicateSuggestions.map((duplicate, index) => {
              const documentUrl = `/workspace/${workspaceDetails?.custom_domain}/${duplicate.document_type.toLowerCase()}/${duplicate.id}`;
              return (
                <div
                  key={index}
                  className="Create__duplicate-item"
                  onClick={() => window.open(documentUrl, "_blank")}
                  style={{ cursor: "pointer" }}
                  title="Click to view this document in a new tab"
                >
                  <div className="Create__duplicate-item-header">
                    <h4>{duplicate.name}</h4>
                    <span className="Create__similarity-badge">
                      {Math.round(duplicate.similarity_score * 100)}% match
                    </span>
                  </div>
                  {duplicate.description && (
                    <p className="Create__duplicate-description">{duplicate.description}</p>
                  )}
                  <div className="Create__duplicate-meta">
                    <div className="Create__duplicate-meta-item">
                      <span className="Create__meta-label">Type:</span>
                      <span className="Create__meta-value">{duplicate.document_type}</span>
                    </div>
                    {duplicate.parent_page && (
                      <div className="Create__duplicate-meta-item">
                        <span className="Create__meta-label">Parent:</span>
                        <span className="Create__meta-value">{duplicate.parent_page}</span>
                      </div>
                    )}
                    <div className="Create__duplicate-meta-item">
                      <span className="Create__meta-label">Updated:</span>
                      <span className="Create__meta-value">
                        {new Date(duplicate.updated_at).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                  <div className="Create__duplicate-scores">
                    <div className="Create__score-bar">
                      <span className="Create__score-label">Title Similarity: {Math.round(duplicate.title_similarity * 100)}%</span>
                      <div className="Create__score-fill" style={{ width: `${duplicate.title_similarity * 100}%` }}></div>
                    </div>
                    {duplicate.description_similarity !== null && (
                      <div className="Create__score-bar">
                        <span className="Create__score-label">Description Similarity: {Math.round(duplicate.description_similarity * 100)}%</span>
                        <div className="Create__score-fill" style={{ width: `${duplicate.description_similarity * 100}%` }}></div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        </div>

        <div className="Create__form-group">
          <label>Short Description: </label>
          <input
            type="text"
            id="description"
            className="Create__input-full-field"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>
        <div className="Create__form-group-helper-text">
          <i
            className="fas fa-info-circle Create__info-icon"
            title="Information"
          ></i>
          <p className="Create__helper-text">
            This description is what invited users will see about this
            space/page.
          </p>
        </div>

        <div className="Create__form-group">
          <label>Make this content public?</label>
          <label className="Create__switch">
            <input type="checkbox" checked={isPublic} onChange={handleToggle} />
            <span className="Create__slider round"></span>
          </label>
        </div>
        <div className="Create__form-group-helper-text">
          <i
            className="fas fa-info-circle Create__info-icon"
            title="Information"
          ></i>
          <p className="Create__helper-text">
            Public content is viewable by anyone that has this document's URL.
          </p>
        </div>

        <div className="Create__form-group">
          <label>Publish this page upon submitting?</label>
          <label className="Create__switch">
            <input
              type="checkbox"
              checked={completed}
              onChange={handleCompletedToggle}
            />
            <span className="Create__slider round"></span>
          </label>
        </div>
        <div className="Create__form-group-helper-text">
          <i
            className="fas fa-info-circle Create__info-icon"
            title="Information"
          ></i>
          <p className="Create__helper-text">
            Whether the document is public or not, this document will not be{" "}
            <strong>viewable</strong> by team members that are "Viewers" by
            default. You can override this default behavior by setting
            permissions for individual users below.
          </p>
        </div>

        {selectedType === "Space" && (
          <>
            <div className="Create__form-group">
              <label>Show a table of contents on this space?</label>
              <label className="Create__switch">
                <input
                  type="checkbox"
                  checked={showTableOfContents}
                  onChange={(e) => setShowTableOfContents(e.target.checked)}
                />
                <span className="Create__slider round"></span>
              </label>
            </div>
            <div className="Create__form-group-helper-text">
              <i
                className="fas fa-info-circle Create__info-icon"
                title="Information"
              ></i>
              <p className="Create__helper-text">
                When enabled, viewers see an "In this space" section near the
                top of the space listing the pages inside it that they have
                access to.
              </p>
            </div>
          </>
        )}

        <div className="Create__form-group">
          <label>Attach File (Optional)</label>
          
          {/* File input for attachment */}
          <input
            type="file"
            id="attachment-upload"
            accept=".zip,.pdf,.jpeg,.png,.mp3,.mp4"
            onChange={handleAttachmentUpload}
            className="Create__file-input"
          />
          <button
            type="button"
            onClick={() => setCcPickerOpen(true)}
            style={{ display: 'block', marginBottom: '10px', marginRight: '12px', background: 'none', border: '1px solid #0d6efd', color: '#0d6efd', borderRadius: '5px', padding: '6px 12px', cursor: 'pointer', whiteSpace: 'nowrap', alignSelf: 'flex-start' }}
          >
            <i className="fas fa-box-open"></i> Choose from Command Center
          </button>

          {/* Show selected attachment */}
          {attachmentFile && (
            <div className="Create__selected-file">
              <button
                type="button"
                onClick={removeAttachment}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  color: "#dc3545",
                  fontSize: "16px",
                  marginLeft: "10px"
                }}
                title="Remove attachment"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
          )}
        
        </div>
        
        <div className="Create__form-group-helper-text">
          <i className="fas fa-info-circle Create__info-icon" title="Information"></i>
          <p className="Create__helper-text">
            You can attach one ZIP, PDF, JPEG, PNG, MP3, or MP4 file to your document. The file size limit is 8MB.
            Users will be able to download this attachment when viewing the document.
          </p>
        </div>

        <div
          className="Create__form-group"
          style={{ flexDirection: "column", marginBottom: "60px" }}
        >
          <label>Document-specific permissions</label>
          <div className="Create__form-group-helper-text">
            <i
              className="fas fa-info-circle Create__info-icon"
              title="Information"
            ></i>
            <p className="Create__helper-text">
              These permissions are specific to this document, overriding
              workspace-level permissions. To learn more about group privlages, please visit <a 
                href="https://bluebird-documentation.com/documentation/page/Workspace%20Permissions/NGzFPNeV9GY1g6q2PFX7SfQmRqw1" 
                target="_blank" 
                rel="noopener noreferrer">this link</a>. 
              <strong>
                <i>
                  &nbsp;To keep existing workspace permissions in place for all team
                  members, leave this field blank.
                </i>
              </strong>
            </p>
          </div>

          {emails.map((member, index) => (
            <div
              key={index}
              className="Create__email-item"
              style={{
                display: "flex",
                alignItems: "center",
                marginBottom: "10px",
              }}
            >
              <span>{member.email}</span>
              <select
                value={member.role}
                className="Create__select"
                onChange={(e) => handleRoleChange(index, e.target.value)}
                style={{ marginLeft: "10px", padding: "5px" }}
              >
                {PERMISSION_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <button
                onClick={() => handleRemoveEmail(index)}
                style={{
                  marginLeft: "10px",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                }}
              >
                <i
                  className="fas fa-trash-alt"
                  title="Remove Team Member"
                  style={{ color: "white" }}
                ></i>
              </button>
            </div>
          ))}

          <div className="Create__email-input-container">
            <input
              type="email"
              className="Create__email-input"
              placeholder="Enter Email Address"
              value={currentEmail}
              onChange={(e) => setCurrentEmail(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleAddEmail()}
            />
            <button onClick={handleAddEmail} className="Create__add-email-btn">
              <i className="fas fa-plus"></i>
            </button>
          </div>
        </div>

        <div
          className="Create__form-group"
          style={{ flexDirection: "column", marginBottom: "60px" }}
        >
          <label>Share outside the workspace</label>
          <div className="Create__form-group-helper-text">
            <i
              className="fas fa-info-circle Create__info-icon"
              title="Information"
            ></i>
            <p className="Create__helper-text">
              People outside this workspace receive an email with a private{" "}
              <strong>view-only</strong> link — they don't need an account to
              open it. Removing them later revokes the link. Workspace members
              should be given document-specific permissions above instead.
            </p>
          </div>

          {externalEmails.map((email, index) => (
            <div
              key={index}
              className="Create__email-item"
              style={{
                display: "flex",
                alignItems: "center",
                marginBottom: "10px",
              }}
            >
              <span>{email}</span>
              <span style={{ marginLeft: "10px", opacity: 0.8, fontSize: "0.85em" }}>
                <i className="fas fa-eye"></i> View only
              </span>
              <button
                onClick={() => handleRemoveExternalEmail(index)}
                style={{
                  marginLeft: "10px",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                }}
              >
                <i
                  className="fas fa-trash-alt"
                  title="Remove external viewer"
                  style={{ color: "white" }}
                ></i>
              </button>
            </div>
          ))}

          <div className="Create__email-input-container">
            <input
              type="email"
              className="Create__email-input"
              placeholder="Enter external email address"
              value={currentExternalEmail}
              onChange={(e) => setCurrentExternalEmail(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleAddExternalEmail()}
            />
            <button onClick={handleAddExternalEmail} className="Create__add-email-btn">
              <i className="fas fa-plus"></i>
            </button>
          </div>
        </div>

        {showSplitView && (
          <div className="EditDocument__split-view-selector">
            <label>Select Side View Document:</label>
            <Select
              options={allDocumentation}
              value={allDocumentation.find(
                (doc) => doc.label === selectedViewDocName
              )}
              onChange={(option) => {
                setSelectedViewDoc(option ? option.value : null);
                setSelectedViewDocName(option ? option.label : "");
              }}
              placeholder="Choose a document..."
              isSearchable={true}
            />
          </div>
        )}

        <div className="EditDocument__edit-document-container">
          <div
            className={`EditDocument__editor-section ${
              selectedViewDoc ? "split" : ""
            }`}
          >
            <div
              className={`EditDocument__quill-container ${
                isFullscreen ? "fullscreen-mode" : ""
              }`}
            >
              <div className="EditDocument__quill-toolbar"></div>
              <div className="QuillOverrides__content-formatting">
              <ReactQuill
                value={content}
                ref={quillRef}
                onChange={setContent}
                modules={modules}
                style={{ height: isFullscreen ? "100vh" : "70vh" }}
              />
              <TaskReferenceDropdown {...taskRefProps} />
              <UserMentionDropdown {...userMentionProps} />
              </div>
              <button
                onClick={toggleFullscreen}
                className="EditDocument__fullscreen-btn EditDocument__fullscreen-btn-corner"
                title={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}
                style={{ bottom: isFullscreen ? "20px" : "-18px" }}
              >
                <i
                  className={`fas ${
                    isFullscreen ? "fa-compress-alt" : "fa-expand-alt"
                  }`}
                ></i>
              </button>
            </div>
          </div>

          {/* Split view implementation if needed */}
          {selectedViewDoc && showSplitView && (
            <div className="EditDocument__view-document-section">
              <div className="EditDocument__view-document-header">
                <p className="medium">
                  <strong>Viewing:</strong> <i>{viewDocTitle}</i>
                </p>
                <button
                  onClick={() => setSelectedViewDoc(null)}
                  className="EditDocument__close-btn"
                >
                  ✖
                </button>
              </div>
              <div className="EditDocument__view-document-content">
                <div dangerouslySetInnerHTML={{ __html: viewDocContent }} />
              </div>
            </div>
          )}
        </div>

        <div className="EditDocument__button-container">          
          <button onClick={handleSubmit} className="submit-button">
            Submit
          </button>
        </div>
      </div>
      {enhancedRightClickEnabled && ContextMenuComponent}
      {templatePrefill.element}
      {templateSnippets.element}
      <CommandCenterFilePickerModal
        isOpen={ccPickerOpen}
        onClose={() => setCcPickerOpen(false)}
        onSelect={(file) => setAttachmentFile(file)}
        allowedExtensions={['.zip', '.pdf', '.jpeg', '.png', '.mp3', '.mp4']}
        maxSizeBytes={8 * 1024 * 1024}
      />
    </div>
  );
}

export default CreateWorkspaceDocument;
